import { useInfiniteQuery } from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import { useEffect } from "react";

import type { JsonLdCollection } from "@luna-restaurant/school-api";

import { useRequest } from "@/hooks/useRequest";

type CollectionRequestOptions = {
  cache?: RequestCache;
};

type UseCollectionOptions = {
  queryKey: readonly unknown[];
  path: string;
  query?: Record<string, string>;
  requestOptions?: CollectionRequestOptions;
  enabled?: boolean;
  staleTime?: number;
  fetchAllPages?: boolean;
};

export const useCollection = <T>(options: UseCollectionOptions) => {
  const request = useRequest();
  const requestOptionsCacheKey = options.requestOptions?.cache ?? null;

  const infiniteQuery = useInfiniteQuery<
    JsonLdCollection<T>,
    Error,
    InfiniteData<JsonLdCollection<T>, string>,
    readonly unknown[],
    string
  >({
    queryKey: [
      ...options.queryKey,
      options.path,
      options.query,
      requestOptionsCacheKey,
    ],
    queryFn: async ({ pageParam }) => {
      const isFirstPage = pageParam === options.path;
      const result = await request<JsonLdCollection<T>>(pageParam, {
        ...(requestOptionsCacheKey ? { cache: requestOptionsCacheKey } : {}),
        query: isFirstPage ? options.query : undefined,
      });

      if (!result) {
        throw new Error(
          `No response received for pageParam "${String(pageParam)}" (path: "${options.path}"${
            isFirstPage && options.query
              ? `, query: ${JSON.stringify(options.query)}`
              : ""
          })`,
        );
      }

      return result;
    },
    initialPageParam: options.path,
    getNextPageParam: (lastPage) => lastPage.view?.next ?? undefined,
    enabled: options.enabled,
    staleTime: options.staleTime,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = infiniteQuery;

  useEffect(() => {
    if (options.fetchAllPages === false) {
      return;
    }
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, options.fetchAllPages]);

  const items = infiniteQuery.data?.pages.flatMap((page) => page.member) ?? [];
  const totalItems = infiniteQuery.data?.pages[0]?.totalItems ?? 0;

  return {
    items,
    totalItems,
    data: infiniteQuery.data,
    dataUpdatedAt: infiniteQuery.dataUpdatedAt,
    error: infiniteQuery.error,
    status: infiniteQuery.status,
    isLoading: infiniteQuery.isLoading,
    isError: infiniteQuery.isError,
    isFetched: infiniteQuery.isFetched,
    isSuccess: infiniteQuery.isSuccess,
    isFetching: infiniteQuery.isFetching,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    refetch: infiniteQuery.refetch,
  };
};
